import { toast } from "@/components/ui";
import { AVAILABLE_ACTIONS_QUERY_KEYS, QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { wildcardInvalidateQuery } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

export interface TakeEncounterParams {
  encounterId: string;
  patientId: string;
  overrideBlock?: boolean;
  /**
   * Whether onError shows an error toast. Callers that render the override
   * dialog set this to false and toast themselves only when no dialog opens,
   * so the toast isn't shown redundantly alongside the dialog.
   */
  showErrorToast?: boolean;
}

export interface TakeEncounterResponse {
  encounterId: string;
}

export interface TakeEncounterErrorItem {
  debugMessage: string;
  errorCode: EncounterAssignmentErrorCodeEnum;
}

export interface TakeEncounterError {
  detail: {
    encounterId: string;
    // Generic server-error fallback message (e.g. on a 500).
    debugMessage?: string;
    // Every blocking assignment failure for the encounter.
    errors?: TakeEncounterErrorItem[];
  };
}

export enum EncounterAssignmentErrorCodeEnum {
  NOT_ON_SHIFT = "NOT_ON_SHIFT",
  NOT_A_PROVIDER = "NOT_A_PROVIDER",
  NOT_MOTHER_ENCOUNTER = "NOT_MOTHER_ENCOUNTER",
  FHIR_ENCOUNTER_FINISHED = "FHIR_ENCOUNTER_FINISHED",
  FHIR_ENCOUNTER_STATUS_NOT_TAKEABLE = "FHIR_ENCOUNTER_STATUS_NOT_TAKEABLE",
  STATE_MACHINE_INTEGRITY = "STATE_MACHINE_INTEGRITY",
  NOT_TAKEABLE_STATE = "NOT_TAKEABLE_STATE",
  NOT_STEALABLE_STATE = "NOT_STEALABLE_STATE",
  NOT_CLEARED_FOR_PARTNER = "NOT_CLEARED_FOR_PARTNER",
  NOT_LICENSED_FOR_STATE = "NOT_LICENSED_FOR_STATE",
  NOT_INSURANCE_ELIGIBLE = "NOT_INSURANCE_ELIGIBLE",
  NOT_QUALIFIED_FOR_PEDS = "NOT_QUALIFIED_FOR_PEDS",
  NOT_LIVE_CHAT_ENCOUNTER = "NOT_LIVE_CHAT_ENCOUNTER",
  STATE_MACHINE_TRANSITION_FAILURE = "STATE_MACHINE_TRANSITION_FAILURE",
  ONE_MEDICAL_ASSIGNMENT_FAILURE = "ONE_MEDICAL_ASSIGNMENT_FAILURE",
  NOT_HANDOFFABLE_WAITING_ROOM = "NOT_HANDOFFABLE_WAITING_ROOM",
}

const takeEncounterErrorMessages: Record<
  EncounterAssignmentErrorCodeEnum,
  string
> = {
  [EncounterAssignmentErrorCodeEnum.NOT_LICENSED_FOR_STATE]:
    "You are not licensed in the correct state to take this visit.",
  [EncounterAssignmentErrorCodeEnum.NOT_INSURANCE_ELIGIBLE]:
    "You are not covered by the patient's insurance to take this visit.",
  [EncounterAssignmentErrorCodeEnum.NOT_QUALIFIED_FOR_PEDS]:
    "You are not qualified to take pediatric patients.",
  [EncounterAssignmentErrorCodeEnum.NOT_ON_SHIFT]:
    "You are currently not on shift. Toggle your shift status to take this visit.",
  [EncounterAssignmentErrorCodeEnum.NOT_A_PROVIDER]:
    "Your account is not classified as a provider.",
  [EncounterAssignmentErrorCodeEnum.NOT_MOTHER_ENCOUNTER]:
    "This visit cannot be taken. Refresh the EMR and try again.",
  [EncounterAssignmentErrorCodeEnum.FHIR_ENCOUNTER_FINISHED]:
    "The visit has already been completed.",
  [EncounterAssignmentErrorCodeEnum.FHIR_ENCOUNTER_STATUS_NOT_TAKEABLE]:
    "The visit is in a status that cannot be taken. Refresh the EMR and try again.",
  [EncounterAssignmentErrorCodeEnum.STATE_MACHINE_INTEGRITY]:
    "An error occurred taking this visit. Refresh the EMR and try again.",
  [EncounterAssignmentErrorCodeEnum.NOT_TAKEABLE_STATE]:
    "The visit is in a status that cannot be taken. Refresh the EMR and try again.",
  [EncounterAssignmentErrorCodeEnum.NOT_STEALABLE_STATE]:
    "The visit is in a status that cannot be stolen. Refresh the EMR and try again.",
  [EncounterAssignmentErrorCodeEnum.NOT_CLEARED_FOR_PARTNER]:
    "Your account is not cleared to take patients for this partner.",
  [EncounterAssignmentErrorCodeEnum.NOT_LIVE_CHAT_ENCOUNTER]:
    "This visit is not a live chat visit. Refresh the EMR and try again.",
  [EncounterAssignmentErrorCodeEnum.STATE_MACHINE_TRANSITION_FAILURE]:
    "An error occurred taking this visit. Refresh the EMR and try again.",
  [EncounterAssignmentErrorCodeEnum.ONE_MEDICAL_ASSIGNMENT_FAILURE]:
    "This One Medical visit can't be taken. Inform the operations team to force-cancel this visit.",
  [EncounterAssignmentErrorCodeEnum.NOT_HANDOFFABLE_WAITING_ROOM]:
    "This visit cannot be handed off. Refresh the EMR and try again.",
};

/** Shared toast id so take/steal errors dedupe rather than stack. */
export const TAKE_ENCOUNTER_ERROR_TOAST_ID = "distribution-take-encounter";

/**
 * Map a take/steal error response to a user-facing message. Assignment
 * failures arrive under `detail.errors`; uses the first known message, falling
 * back to a generic server-error message.
 */
export const getTakeEncounterErrorMessage = (
  error: unknown,
): string | undefined => {
  const detail = (error as TakeEncounterError | undefined)?.detail;
  const errorCode = detail?.errors?.[0]?.errorCode;
  const debugMessage =
    detail?.debugMessage ?? detail?.errors?.[0]?.debugMessage;
  return (errorCode && takeEncounterErrorMessages[errorCode]) || debugMessage;
};

export const useTakeEncounter = ({
  encounterId,
  patientId,
  showErrorToast = true,
}: TakeEncounterParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<TakeEncounterResponse, boolean, TakeEncounterError>({
    mutationOptions: {
      mutationKey: QueryKeys.Distribution.TAKE,
      mutationFn: (overrideBlock: boolean) => {
        return axiosInstance.post("/new-api/distribution/take-encounter", {
          encounterId,
          patientId,
          overrideBlock,
        });
      },
      onError: (error) => {
        if (showErrorToast) {
          toast.error(getTakeEncounterErrorMessage(error.response?.data), {
            id: TAKE_ENCOUNTER_ERROR_TOAST_ID,
          });
        }
        throw error.response?.data;
      },
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: QueryKeys.Provider.PATIENT_TABS,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Timeline.ENCOUNTERS({
            patientId,
          }),
        });

        for (const queryKey of AVAILABLE_ACTIONS_QUERY_KEYS) {
          wildcardInvalidateQuery(
            queryClient,
            queryKey.filter((key) => key !== undefined),
          );
        }
        toast.success("Encounter Taken");
      },
    },
  });
};
